import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "../shared/button";
import { ConfirmDialog } from "../shared/confirm-dialog";
import { apiPost, apiGet, endpoints } from "../../api/client";
import type { PairResponse, PairStatusResponse } from "../../api/types";
import "./pair-flow.css";

type PairState = "unpaired" | "code-shown" | "paired";

interface PairFlowProps {
  deviceId: string | null;
  onPaired: (deviceId: string) => void;
  onUnpaired: () => void;
}

export function PairFlow({ deviceId, onPaired, onUnpaired }: PairFlowProps) {
  const [state, setState] = useState<PairState>(
    deviceId ? "paired" : "unpaired",
  );
  const [code, setCode] = useState<string>("");
  const [statusText, setStatusText] = useState("Waiting for Terminal...");
  const [error, setError] = useState<string | null>(null);
  const [showUnpairConfirm, setShowUnpairConfirm] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  async function generateCode() {
    setError(null);
    try {
      const res = await apiPost<PairResponse>(endpoints.pair);
      setCode(res.code);
      setState("code-shown");
      setStatusText("Waiting for Terminal...");

      pollRef.current = setInterval(async () => {
        try {
          const status = await apiGet<PairStatusResponse>(
            endpoints.pairStatus(res.device_code_id),
          );
          if (status.status === "PAIRED" && status.device_id) {
            stopPolling();
            setState("paired");
            onPaired(status.device_id);
          } else if (status.status === "EXPIRED") {
            stopPolling();
            setStatusText("Code expired — please start over");
          }
        } catch {
          // Ignore individual poll errors
        }
      }, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate code");
    }
  }

  function resetPairing() {
    stopPolling();
    setState("unpaired");
    setCode("");
    setError(null);
  }

  function confirmUnpair() {
    stopPolling();
    setState("unpaired");
    setCode("");
    onUnpaired();
  }

  return (
    <div className="term-wrap">
      <div className="clabel">Square Terminal Pairing</div>

      {error && (
        <div style={{ background: "var(--red-bg)", border: "1px solid var(--red-bd)", color: "var(--red)", padding: "10px 14px", borderRadius: "10px", fontSize: "13px", fontWeight: 600, marginBottom: "12px" }}>
          {error}
        </div>
      )}

      {state === "unpaired" && (
        <div className="card">
          <div style={{ fontSize: "13px", color: "var(--ink2)", lineHeight: 1.7, marginBottom: "14px" }}>
            1. Make sure your Square Terminal is on and connected to Wi-Fi
            <br />
            2. Tap <strong>Generate Code</strong> below
            <br />
            3. On Terminal: <strong>Settings &rarr; Point of Sale &rarr; Enter Pairing Code</strong>
            <br />
            4. Enter the code shown here
          </div>
          <Button full onClick={generateCode}>
            Generate Pairing Code
          </Button>
        </div>
      )}

      {state === "code-shown" && (
        <div className="card">
          <div className="clabel">Enter this code on your Terminal</div>
          <div className="pair-code-display">{code}</div>
          <div style={{ textAlign: "center", color: "var(--ink3)", fontSize: "13px", margin: "12px 0" }}>
            {statusText}
          </div>
          <Button variant="outline" full onClick={resetPairing}>
            Start Over
          </Button>
        </div>
      )}

      {state === "paired" && (
        <div className="card">
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <div style={{ fontSize: "40px", marginBottom: "10px" }}>&#x2705;</div>
            <div style={{ fontSize: "17px", fontWeight: 800, marginBottom: "6px" }}>
              Terminal Paired
            </div>
            <div style={{ fontSize: "12px", color: "var(--ink3)", marginBottom: "16px" }}>
              Device: {deviceId}
            </div>
            <Button variant="red" onClick={() => setShowUnpairConfirm(true)}>
              Unpair Terminal
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={showUnpairConfirm}
        title="Unpair Terminal?"
        message="The Terminal will stop receiving checkouts from this app until you pair again."
        confirmLabel="Unpair"
        variant="destructive"
        onConfirm={() => {
          setShowUnpairConfirm(false);
          confirmUnpair();
        }}
        onCancel={() => setShowUnpairConfirm(false)}
      />
    </div>
  );
}
